import React, { useEffect, useRef, useState } from 'react';
import api, { API } from '../../lib/api';
import ActivityFeed from '../ActivityFeed';
import MentionInput from '../MentionInput';
import Portal from '../Portal';
import { X, Plus } from 'lucide-react';
import Avatar from './Avatar';
import AttachmentsSection from './AttachmentsSection';
import CommentsSection from './CommentsSection';
import Sidebar from './Sidebar';
import SuggestDeleteModal from './SuggestDeleteModal';
import { useChatSocket } from '../../hooks/useChatSocket';
import PresenceIndicator from '../PresenceIndicator';
import { usePermission } from '../../lib/permissions';

export default function TaskDetail({ taskId, project, currentUser, lists: initialLists, onClose, onChange }) {
  const [activeTab, setActiveTab] = useState('comments');
  const { has } = usePermission();
  const [task, setTask] = useState(null);
  const [entries, setEntries] = useState([]);
  const [lists, setLists] = useState(initialLists || []);
  const [members, setMembers] = useState([]);
  const [showAssign, setShowAssign] = useState(false);
  const [checkTitle, setCheckTitle] = useState('');
  const [uploading, setUploading] = useState(false);
  const [deleteReason, setDeleteReason] = useState('');
  const [suggestOpen, setSuggestOpen] = useState(false);
  const [mySuggestion, setMySuggestion] = useState(null);
  const [comments, setComments] = useState([]);
  const [commentDraft, setCommentDraft] = useState('');
  const [commentBusy, setCommentBusy] = useState(false);
  const [projectTasks, setProjectTasks] = useState([]);
  const [quality, setQuality] = useState(null);
  const [memberSearchQuery, setMemberSearchQuery] = useState('');
  const [projectState, setProjectState] = useState(project || null);

  useEffect(() => {
    if (project) {
      setProjectState(project);
    }
  }, [project]);

  const { socket } = useChatSocket();
  const [viewers, setViewers] = useState([]);
  const [typingUsers, setTypingUsers] = useState([]);

  const reload = async () => {
    const { data } = await api.get(`/tasks/${taskId}`);
    const t = data.task;
    setTask(t);
    const time = await api.get(`/tasks/${taskId}/time`);
    setEntries(time.data.entries);
    if (t.project_id) {
      try {
        const p = await api.get(`/projects/${t.project_id}`);
        // Prefer assignable_users (roster incl. workspace users when caller can manage); fall back to members.
        setMembers(p.data.assignable_users || p.data.members || []);
        setProjectState(p.data.project);
      } catch { /* noop */ }
      if (!initialLists || !initialLists.length) {
        try {
          const l = await api.get(`/lists/project/${t.project_id}`);
          setLists(l.data.lists || []);
        } catch { /* noop */ }
      }
    }
    try {
      const cr = await api.get(`/comments/task/${taskId}`);
      setComments(cr.data.comments || []);
    } catch { /* noop */ }
    try {
      const pt = await api.get(`/tasks?project_id=${t.project_id}`);
      setProjectTasks((pt.data.tasks || []).filter((x) => x.id !== taskId));
    } catch { /* noop */ }
    try {
      const qr = await api.get(`/rewards/quality/${taskId}`);
      setQuality(qr.data.rating);
    } catch { /* noop */ }
    try {
      const ds = await api.get(`/rewards/deletion-suggestions?status=pending`);
      const mine = (ds.data.suggestions || []).find((s) => s.task_id === taskId && s.requester_id === currentUser.id);
      setMySuggestion(mine || null);
    } catch { /* noop */ }
  };
  useEffect(() => { reload(); }, [taskId]);

  useEffect(() => {
    if (!socket || !taskId) return;
    socket.emit('task:viewing', { taskId });

    const handlePresence = (data) => {
      if (data.taskId === taskId && data.userId !== currentUser.id) {
        if (data.action === 'viewing') {
          const user = members.find((m) => m.id === data.userId) || { id: data.userId, full_name: 'Someone', email: 'user@example.com' };
          setViewers((prev) => (prev.find((v) => v.id === user.id) ? prev : [...prev, user]));
        }
      }
    };

    const handleTyping = (data) => {
      if (data.taskId === taskId && data.userId !== currentUser.id) {
        const user = members.find((m) => m.id === data.userId) || { id: data.userId, full_name: 'Someone', email: 'user@example.com' };
        if (data.isTyping) {
          setTypingUsers((prev) => (prev.find((v) => v.id === user.id) ? prev : [...prev, user]));
        } else {
          setTypingUsers((prev) => prev.filter((v) => v.id !== user.id));
        }
      }
    };

    socket.on('task:presence', handlePresence);
    socket.on('task:typing', handleTyping);

    return () => {
      socket.off('task:presence', handlePresence);
      socket.off('task:typing', handleTyping);
    };
  }, [socket, taskId, members, currentUser.id]);

  const submitComment = async () => {
    if (!commentDraft.trim() || commentBusy) return;
    setCommentBusy(true);
    try {
      const { data } = await api.post(`/comments/task/${taskId}`, { body: commentDraft.trim() });
      setComments((prev) => [...prev, data.comment]);
      setCommentDraft('');
    } catch (e) { alert(e.response?.data?.error || 'Failed to post comment'); }
    finally { setCommentBusy(false); }
  };
  const deleteComment = async (id) => {
    if (!confirm('Delete this comment?')) return;
    await api.delete(`/comments/${id}`);
    setComments((prev) => prev.filter((c) => c.id !== id));
  };

  if (!task) {
    return (
      <Portal>
        <div className="tm-modal-overlay" onClick={onClose}>
          <div className="tm-modal" onClick={(e) => e.stopPropagation()} style={{ textAlign: 'center', padding: 40 }}>
            <div className="tm-loader" style={{ margin: '0 auto' }} />
          </div>
        </div>
      </Portal>
    );
  }

  const isLeader = currentUser.role === 'leader';
  const projMgrId = projectState?.delegator_id || task.manager_id;
  const isManagerOfProject = currentUser.role === 'manager' && projMgrId === currentUser.id;
  const isAssignee = (task.assignees || []).some((a) => a.id === currentUser.id);
  const canManage = isLeader || isManagerOfProject;
  const canEditLimited = isAssignee && currentUser.role === 'team';

  const save = async (patch) => { const { data } = await api.patch(`/tasks/${task.id}`, patch); setTask(data.task); onChange?.(data.task); };
  const toggleAssignee = async (uid) => {
    const cur = new Set((task.assignees || []).map((a) => a.id));
    if (cur.has(uid)) cur.delete(uid); else cur.add(uid);
    await save({ assignee_ids: [...cur] });
  };
  const addChecklist = async () => {
    if (!checkTitle.trim()) return;
    await api.post(`/tasks/${task.id}/checklist`, { title: checkTitle.trim() });
    setCheckTitle(''); reload();
  };
  const toggleCheck = async (item) => { await api.patch(`/tasks/checklist/${item.id}`, { completed: !item.completed }); reload(); };
  const delCheck = async (item) => { await api.delete(`/tasks/checklist/${item.id}`); reload(); };
  const addAttachmentLink = async (name, url) => {
    try { await api.post(`/tasks/${task.id}/attachments`, { name, url }); reload(); return true; }
    catch (e) { alert(e.response?.data?.error || 'Failed'); return false; }
  };
  const uploadFile = async (file) => {
    setUploading(true);
    try {
      const fd = new FormData(); fd.append('file', file);
      const t = localStorage.getItem('tm_jwt');
      await fetch(`${API}/uploads/task/${task.id}`, {
        method: 'POST', body: fd, credentials: 'include',
        headers: t ? { Authorization: `Bearer ${t}` } : {},
      }).then((r) => r.ok ? r.json() : r.json().then((e) => Promise.reject(e)));
      reload();
    } catch (e) { alert(e.error || 'Upload failed'); }
    finally { setUploading(false); }
  };
  const delAtt = async (a) => {
    const confirmed = window.confirm(
      `Are you sure you want to delete "${a.name}"?`
    );

    if (!confirmed) return;
    await api.delete(`/tasks/attachments/${a.id}`); reload();
  };
  const requestOrDelete = async () => {
    if (isLeader) {
      if (!confirm('Delete this task permanently?')) return;
      await api.delete(`/tasks/${task.id}`); onChange?.(); onClose(); return;
    }
    if (!confirm('Send delete request to Leader for approval?')) return;
    await api.delete(`/tasks/${task.id}`, { data: { reason: deleteReason } });
    reload(); onChange?.();
  };

  const totalTime = entries.reduce((s, e) => s + (e.duration_seconds || 0), 0);

  return (
    <Portal>
      <div className="tm-modal-overlay" onClick={onClose}>
        <div className="tm-modal" style={{ maxWidth: 720 }} onClick={(e) => e.stopPropagation()} data-testid="task-detail-modal">
          <div className="flex items-start justify-between mb-2">
            <div style={{ flex: 1, marginRight: 16 }}>
              <input className="tm-input" style={{ fontSize: 20, fontWeight: 600, border: 'none', background: 'transparent', padding: 0, width: '100%' }}
                value={task.title} onChange={(e) => setTask({ ...task, title: e.target.value })}
                onBlur={() => save({ title: task.title })}
                disabled={!(canManage || (currentUser.role === 'manager' && isAssignee))}
                data-testid="detail-title" />
            </div>
            <div className="flex items-center gap-2">
              <PresenceIndicator viewers={viewers} typingUsers={typingUsers} />
              <button onClick={onClose} className="tm-btn-ghost" data-testid="detail-close"><X size={18} /></button>
            </div>
          </div>
          <div style={{ fontSize: 12, color: 'var(--trello-text-muted)', marginBottom: 16 }}>
            Project: <b>{projectState?.name || "—"}</b> ·  in list: <b>{(lists || []).find((l) => l.id === task.list_id)?.name || "—"}</b>

            {/* <b>{task.list_name || '—'}</b> · project <b>{project.project_name}</b> */}
            {!!task.pending_delete && (
              <span style={{ marginLeft: 8, background: 'var(--trello-list-bg)', color: '#DE350B', padding: '2px 8px', borderRadius: 3, fontWeight: 600 }}>
                ⚠ Delete pending Leader approval
              </span>
            )}
          </div>

          <div className="grid gap-4" style={{ gridTemplateColumns: '1fr 220px' }}>
            <div className="space-y-4">
              {/* Members */}
              <div>
                <div className="flex items-center justify-between">
                  <div className="tm-label" style={{ marginBottom: 4 }}>Members</div>
                  {(canManage || has('tasks.assign')) && <button className="tm-btn-ghost" onClick={() => { setMemberSearchQuery(''); setShowAssign((v) => !v); }} data-testid="assignee-toggle"><Plus size={14} /></button>}
                </div>
                <div className="flex flex-wrap gap-1 relative">
                  {(task.assignees || []).map((a) => <Avatar key={a.id} user={a} size={32} />)}
                  {(task.assignees || []).length === 0 && <span style={{ fontSize: 13, color: 'var(--trello-text-muted)' }}>No one assigned</span>}
                  {showAssign && (
                    <div className="tm-pop" style={{ position: 'absolute', top: 40, left: 0, zIndex: 20, minWidth: 240 }} data-testid="assignee-pop">
                      <div className="tm-label">Assign to</div>
                      <input
                        type="text"
                        placeholder="Search member..."
                        value={memberSearchQuery}
                        onChange={(e) => setMemberSearchQuery(e.target.value)}
                        className="tm-input mb-2"
                        style={{ padding: '4px 8px', fontSize: 12, width: '100%', boxSizing: 'border-box' }}
                        data-testid="assignee-search"
                      />
                      {members
                        .filter(m => (m.full_name || m.email || '').toLowerCase().includes(memberSearchQuery.toLowerCase()))
                        .map((m) => {
                          const on = (task.assignees || []).some((a) => a.id === m.id);
                          return (
                            <label key={m.id} className="flex items-center gap-2 p-1 cursor-pointer" style={{ borderRadius: 4 }} onMouseDown={(e) => e.preventDefault()}>
                              <input type="checkbox" checked={on} onChange={() => toggleAssignee(m.id)} data-testid={`assign-${m.id}`} />
                              <Avatar user={m} size={22} />
                              <span style={{ fontSize: 13 }}>{m.full_name} <span style={{ color: 'var(--trello-text-muted)' }}>({m.role})</span></span>
                            </label>
                          );
                        })}
                      {members.filter(m => (m.full_name || m.email || '').toLowerCase().includes(memberSearchQuery.toLowerCase())).length === 0 && (
                        <div style={{ padding: 8, fontSize: 12, color: 'var(--trello-text-muted)', textAlign: 'center' }}>No matches found</div>
                      )}
                      <button className="tm-btn-ghost mt-1" onClick={() => setShowAssign(false)}>Done</button>
                    </div>
                  )}
                </div>
              </div>

              {/* Description */}
              <div>
                <div className="tm-label">Description</div>
                <MentionInput
                  value={task.description || ''}
                  onChange={(v) => setTask({ ...task, description: v })}
                  onBlur={() => save({ description: task.description })}
                  members={members}
                  disabled={currentUser.role === 'team'}
                  placeholder="Add a description. Type @ to mention someone…"
                  testId="detail-desc"
                />
              </div>

              {/* Checklist */}
              <div>
                <div className="tm-label">Checklist {task.checklist?.length ? `(${task.checklist.filter((c) => c.completed).length}/${task.checklist.length})` : ''}</div>
                {task.checklist?.length > 0 && (
                  <div className="tm-mini-progress mb-2">
                    <div style={{ width: `${(task.checklist.filter((c) => c.completed).length / task.checklist.length) * 100}%` }} />
                  </div>
                )}
                <div className="space-y-1">
                  {(task.checklist || []).map((c) => (
                    <div key={c.id} className="flex items-center gap-2" data-testid={`check-${c.id}`}>
                      <input type="checkbox" checked={!!c.completed} onChange={() => toggleCheck(c)} />
                      <span style={{ flex: 1, fontSize: 14, textDecoration: c.completed ? 'line-through' : 'none', color: c.completed ? 'var(--trello-text-muted)' : 'inherit' }}>{c.title}</span>
                      {(canManage || currentUser.role === 'manager') && (
                        <button className="tm-btn-ghost" style={{ padding: 2 }} onClick={() => delCheck(c)}><X size={12} /></button>
                      )}
                    </div>
                  ))}
                </div>
                <div className="flex gap-2 mt-2">
                  <input className="tm-input" placeholder="Add checklist item" value={checkTitle} onChange={(e) => setCheckTitle(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && addChecklist()} data-testid="check-input" />
                  <button className="tm-btn" onClick={addChecklist} data-testid="check-add">Add</button>
                </div>
              </div>
              {/* attachments */}
              {/* <AttachmentsSection
                task={task} canManage={canManage} uploading={uploading}
                onUploadFile={uploadFile} onAddLink={addAttachmentLink} onDelete={delAtt}
              /> */}

              {entries.length > 0 && (
                <div>
                  <div className="tm-label">Time entries · total {Math.floor(totalTime / 60)}m</div>
                  <div style={{ maxHeight: 140, overflowY: 'auto', background: 'var(--trello-card-bg)', borderRadius: 6, padding: 8 }}>
                    {entries.map((e) => (
                      <div key={e.id} className="flex justify-between" style={{ fontSize: 13, padding: '4px 0' }}>
                        <span>{e.user_name}</span>
                        <span>{new Date(e.started_at).toLocaleString()}</span>
                        <span style={{ fontWeight: 600 }}>{e.duration_seconds ? `${Math.floor(e.duration_seconds / 60)}m` : '⏱ running'}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* <CommentsSection
                comments={comments}
                commentDraft={commentDraft} setCommentDraft={setCommentDraft}
                submitComment={submitComment} deleteComment={deleteComment}
                commentBusy={commentBusy} members={members} currentUser={currentUser}
                onTypingStart={() => socket?.emit('task:typing:start', { taskId })}
                onTypingStop={() => socket?.emit('task:typing:stop', { taskId })}
              /> */}

              {/* <div style={{ display: 'none' }}>
                <div className="tm-label">Activity</div>
                <ActivityFeed taskId={task.id} refreshKey={task.updated_at || task.id} />
              </div> */}


              <div className="tm-tabs">

                {/* Tab Header */}
                <div className="tm-tab-header">
                  <button
                    className={activeTab === 'comments' ? 'active' : ''}
                    onClick={() => setActiveTab('comments')}
                  >
                    Comments
                  </button>

                  <button
                    className={activeTab === 'attachments' ? 'active' : ''}
                    onClick={() => setActiveTab('attachments')}
                  >
                    Attachments
                  </button>
                  <button
                    className={activeTab === 'activity' ? 'active' : ''}
                    onClick={() => setActiveTab('activity')}
                  >
                    Activity
                  </button>
                </div>


                {/* Comments Tab */}
                {activeTab === 'comments' && (
                  <div className="tm-tab-content">
                    <CommentsSection
                      comments={comments}
                      commentDraft={commentDraft}
                      setCommentDraft={setCommentDraft}
                      submitComment={submitComment}
                      deleteComment={deleteComment}
                      commentBusy={commentBusy}
                      members={members}
                      currentUser={currentUser}
                      onTypingStart={() =>
                        socket?.emit('task:typing:start', { taskId })
                      }
                      onTypingStop={() =>
                        socket?.emit('task:typing:stop', { taskId })
                      }
                    />
                  </div>
                )}

                {/* Attachments Tab */}
                {activeTab === 'attachments' && (
                  <div className="tm-tab-content">
                    {/* <div className="tm-label">Attachments</div> */}

                    <AttachmentsSection
                      task={task} canManage={canManage} uploading={uploading}
                      onUploadFile={uploadFile} onAddLink={addAttachmentLink} onDelete={delAtt}
                    />
                  </div>
                )}

                {/* Activity Tab */}
                {activeTab === 'activity' && (
                  <div className="tm-tab-content">
                    <div className="tm-label">Activity</div>

                    <ActivityFeed
                      taskId={task.id}
                      refreshKey={task.updated_at || task.id}
                    />
                  </div>
                )}

              </div>
            </div>

            <Sidebar
              task={task} save={save}
              canManage={canManage} canEditLimited={canEditLimited} isAssignee={isAssignee} isLeader={isLeader}
              project={projectState} projectTasks={projectTasks} currentUser={currentUser}
              quality={quality} lists={lists} reload={reload}
              mySuggestion={mySuggestion}
              requestOrDelete={requestOrDelete}
              deleteReason={deleteReason} setDeleteReason={setDeleteReason}
              onSuggestDelete={() => setSuggestOpen(true)}
              onClose={onClose} onChange={onChange}
            />
          </div>
        </div>
        {suggestOpen && (
          <SuggestDeleteModal taskId={task.id}
            onClose={() => setSuggestOpen(false)}
            onSubmitted={reload} />
        )}
      </div>
    </Portal>
  );
}
